import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import styled from 'styled-components';
import JsonEditor from './JsonEditor';
import { PromptProfile } from '../api/promptManagement';
import TemplateService from '../services/templateService';

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 16px;
  border-bottom: 1px solid #e1e5e9;
`;

const Title = styled.h3`
  margin: 0;
  color: #2c3e50;
  font-size: 1.5rem;
  font-weight: 600;
`;

const ModeToggle = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ToggleButton = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  border: 2px solid ${(props: { $active: boolean }) => props.$active ? '#667eea' : '#e1e5e9'};
  border-radius: 6px;
  background: ${(props: { $active: boolean }) => props.$active ? '#667eea' : 'white'};
  color: ${(props: { $active: boolean }) => props.$active ? 'white' : '#2c3e50'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${(props: { $active: boolean }) => props.$active ? '#5a6fd8' : '#f8f9fa'};
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
`;

const JsonContainer = styled.div`
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  overflow: hidden;
  flex: 1;
  min-height: 600px;
  display: flex;
  flex-direction: column;
`;

const ActionsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-top: 1px solid #e1e5e9;
  gap: 12px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 10px 20px;
  border: 2px solid ${(props: { $variant?: 'primary' | 'secondary' | 'danger' }) => {
    if (props.$variant === 'primary') return '#667eea';
    if (props.$variant === 'danger') return '#e74c3c';
    return '#e1e5e9';
  }};
  border-radius: 6px;
  background: ${(props: { $variant?: 'primary' | 'secondary' | 'danger' }) => {
    if (props.$variant === 'primary') return '#667eea';
    if (props.$variant === 'danger') return '#e74c3c';
    return 'white';
  }};
  color: ${(props: { $variant?: 'primary' | 'secondary' | 'danger' }) => props.$variant ? 'white' : '#2c3e50'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${(props: { $variant?: 'primary' | 'secondary' | 'danger' }) => {
      if (props.$variant === 'primary') return '#5a6fd8';
      if (props.$variant === 'danger') return '#c0392b';
      return '#f8f9fa';
    }};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusMessage = styled.div<{ $type: 'success' | 'error' | 'info' }>`
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  background: ${(props: { $type: 'success' | 'error' | 'info' }) => {
    if (props.$type === 'success') return '#d4edda';
    if (props.$type === 'error') return '#f8d7da';
    return '#d1ecf1';
  }};
  color: ${(props: { $type: 'success' | 'error' | 'info' }) => {
    if (props.$type === 'success') return '#155724';
    if (props.$type === 'error') return '#721c24';
    return '#0c5460';
  }};
  border: 1px solid ${(props: { $type: 'success' | 'error' | 'info' }) => {
    if (props.$type === 'success') return '#c3e6cb';
    if (props.$type === 'error') return '#f5c6cb';
    return '#bee5eb';
  }};
`;

const TemplateSelector = styled.select`
  padding: 8px 12px;
  border: 2px solid #e1e5e9;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  min-width: 200px;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const TemplateContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

// Get templates from TemplateService
const PROMPT_PROFILE_TEMPLATES = TemplateService.getTemplateNames().reduce((acc, name) => {
  acc[name] = TemplateService.getTemplateAsJson(name);
  return acc;
}, {} as Record<string, string>);

// Validation schema for prompt profiles
const PROMPT_PROFILE_VALIDATION_SCHEMA = {
  required: ['name', 'prompt_type', 'asset_type'],
  properties: {
    name: { type: 'string', minLength: 1 },
    prompt_type: { enum: ['image', 'video'] },
    asset_type: { type: 'string', minLength: 1 },
    description: { type: 'string' },
    // version: auto-managed by backend
    system_role_definition: { type: 'string' },
    custom_system_instructions: { type: 'string' },
    custom_user_instructions: { type: 'string' },
    custom_output_format: { type: 'string' },
    custom_metadata_schema: { type: 'object' },
    custom_system_focus_areas: { type: 'object' },
    custom_user_focus_areas: { type: 'object' },
    llm_parameters: { type: 'object' },
    validation_rules: { type: 'object' }
  }
};

interface JsonPromptProfileEditorProps {
  profile?: PromptProfile;
  onSave: (profile: PromptProfile) => Promise<void>;
  onCancel: () => void;
  mode: 'create' | 'edit';
  onRef?: (ref: any) => void;
}

export const JsonPromptProfileEditor = forwardRef<any, JsonPromptProfileEditorProps>(({
  profile,
  onSave,
  onCancel,
  mode,
  onRef
}, ref) => {
  const [jsonValue, setJsonValue] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Expose save function to parent component
  useImperativeHandle(ref, () => ({
    handleSave: async () => {
      if (!isValid) {
        setStatusMessage({
          type: 'error',
          message: 'Please fix validation errors before saving'
        });
        return;
      }

      try {
        setIsSaving(true);
        const parsedProfile = JSON.parse(jsonValue);
        
        // Debug: Log the parsed profile to see what version is being read
        console.log('🔍 JSON Editor - Parsed profile version:', parsedProfile.version);
        console.log('🔍 JSON Editor - Current profile version:', profile?.version);
        
        // Convert JSON back to PromptProfile format
        const profileData: any = {
          profile_id: profile?.profile_id || '',
          name: parsedProfile.name,
          prompt_type: parsedProfile.prompt_type,
          asset_type: parsedProfile.asset_type,
          description: parsedProfile.description,
          system_role_definition: parsedProfile.system_role_definition,
          custom_system_instructions: parsedProfile.custom_system_instructions,
          custom_user_instructions: parsedProfile.custom_user_instructions,
          custom_output_format: parsedProfile.custom_output_format,
          custom_metadata_schema: parsedProfile.custom_metadata_schema,
          custom_system_focus_areas: parsedProfile.custom_system_focus_areas,
          custom_user_focus_areas: parsedProfile.custom_user_focus_areas,
          custom_focus_areas: parsedProfile.custom_focus_areas,
          llm_parameters: parsedProfile.llm_parameters,
          validation_rules: parsedProfile.validation_rules,
          allowed_project_ids: parsedProfile.allowed_project_ids,
          project_id: parsedProfile.project_id,
          is_default: parsedProfile.is_default
        };

        // Only include version if explicitly provided in JSON (manual version control)
        // If no version in JSON, backend will auto-update
        if (parsedProfile.version !== undefined && parsedProfile.version !== null) {
          profileData.version = parsedProfile.version;
          console.log('🔍 JSON Editor - Manual version control:', parsedProfile.version);
        } else {
          console.log('🔍 JSON Editor - Auto-update mode (no version in JSON)');
        }
        
        // Debug: Log the final profile data version
        console.log('🔍 JSON Editor - Final profile data version:', profileData.version);

        await onSave(profileData);
        
        setStatusMessage({
          type: 'success',
          message: `Profile ${mode === 'create' ? 'created' : 'updated'} successfully!`
        });
      } catch (error) {
        setStatusMessage({
          type: 'error',
          message: `Failed to save profile: ${error instanceof Error ? error.message : 'Unknown error'}`
        });
      } finally {
        setIsSaving(false);
      }
    }
  }), [jsonValue, isValid, profile, mode, onSave]);

  // Initialize JSON value from profile
  useEffect(() => {
    if (profile && mode === 'edit') {
      // Load current profile data into JSON
      // Don't include version by default to enable auto-update
      const profileJson = {
        name: profile.name,
        prompt_type: profile.prompt_type,
        asset_type: profile.asset_type,
        description: profile.description,
        system_role_definition: profile.system_role_definition,
        custom_system_instructions: profile.custom_system_instructions,
        custom_user_instructions: profile.custom_user_instructions,
        custom_output_format: profile.custom_output_format,
        custom_metadata_schema: profile.custom_metadata_schema,
        custom_system_focus_areas: profile.custom_system_focus_areas,
        custom_user_focus_areas: profile.custom_user_focus_areas,
        custom_focus_areas: profile.custom_focus_areas,
        llm_parameters: profile.llm_parameters,
        validation_rules: profile.validation_rules,
        allowed_project_ids: profile.allowed_project_ids,
        project_id: profile.project_id,
        is_default: profile.is_default
        // Note: version not included by default - enables auto-update
        // User can manually add "version": "1.0.1" to JSON for manual control
      };
      setJsonValue(JSON.stringify(profileJson, null, 2));
    } else {
      // Set default template for new profiles - version will be generated by backend
      setJsonValue(TemplateService.getTemplateAsJson('Basic Image Analysis'));
    }
  }, [profile, mode]);

  // Validate JSON and update status
  const validateJson = useCallback((value: string) => {
    try {
      if (!value.trim()) {
        setIsValid(false);
        return false;
      }
      
      const parsed = JSON.parse(value);
      
      // Check required fields
      const required = PROMPT_PROFILE_VALIDATION_SCHEMA.required;
      const missingFields = required.filter(field => !parsed[field]);
      
      if (missingFields.length > 0) {
        setIsValid(false);
        setStatusMessage({
          type: 'error',
          message: `Missing required fields: ${missingFields.join(', ')}`
        });
        return false;
      }
      
      setIsValid(true);
      setStatusMessage(null);
      return true;
    } catch (error) {
      setIsValid(false);
      setStatusMessage({
        type: 'error',
        message: `Invalid JSON: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
      return false;
    }
  }, []);

  const handleJsonChange = (value: string) => {
    setJsonValue(value);
    validateJson(value);
  };

  const handleSave = async () => {
    if (!isValid) {
      setStatusMessage({
        type: 'error',
        message: 'Please fix validation errors before saving'
      });
      return;
    }

    try {
      setIsSaving(true);
      const parsedProfile = JSON.parse(jsonValue);
      
      // Convert JSON back to PromptProfile format
      const profileData: any = {
        profile_id: profile?.profile_id || '',
        name: parsedProfile.name,
        prompt_type: parsedProfile.prompt_type,
        asset_type: parsedProfile.asset_type,
        description: parsedProfile.description,
        system_role_definition: parsedProfile.system_role_definition,
        custom_system_instructions: parsedProfile.custom_system_instructions,
        custom_user_instructions: parsedProfile.custom_user_instructions,
        custom_output_format: parsedProfile.custom_output_format,
        custom_metadata_schema: parsedProfile.custom_metadata_schema,
        custom_system_focus_areas: parsedProfile.custom_system_focus_areas,
        custom_user_focus_areas: parsedProfile.custom_user_focus_areas,
        custom_focus_areas: parsedProfile.custom_focus_areas,
        llm_parameters: parsedProfile.llm_parameters,
        validation_rules: parsedProfile.validation_rules,
        allowed_project_ids: parsedProfile.allowed_project_ids,
        project_id: parsedProfile.project_id,
        is_default: parsedProfile.is_default
      };

      // Only include version if explicitly provided in JSON (manual version control)
      // If no version in JSON, backend will auto-update
      if (parsedProfile.version !== undefined && parsedProfile.version !== null) {
        profileData.version = parsedProfile.version;
        console.log('🔍 JSON Editor - Manual version control:', parsedProfile.version);
      } else {
        console.log('🔍 JSON Editor - Auto-update mode (no version in JSON)');
      }

      await onSave(profileData);
      
      setStatusMessage({
        type: 'success',
        message: `Profile ${mode === 'create' ? 'created' : 'updated'} successfully!`
      });
    } catch (error) {
      setStatusMessage({
        type: 'error',
        message: `Failed to save profile: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTemplateChange = (templateName: string) => {
    if (templateName) {
      try {
        const templateJson = TemplateService.getTemplateAsJson(templateName);
        setJsonValue(templateJson);
        validateJson(templateJson);
      } catch (error) {
        console.error('Error loading template:', error);
        setStatusMessage({
          type: 'error',
          message: `Failed to load template: ${error instanceof Error ? error.message : 'Unknown error'}`
        });
      }
    }
  };

  const handleExport = () => {
    const blob = new Blob([jsonValue], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${profile?.name || 'prompt-profile'}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    setStatusMessage({
      type: 'success',
      message: 'Profile exported successfully!'
    });
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // Reset the file input to allow selecting the same file again
    event.target.value = '';

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        
        if (!content || content.trim() === '') {
          setStatusMessage({
            type: 'error',
            message: 'The selected file is empty or could not be read.'
          });
          return;
        }

        // Parse and validate JSON
        let parsedJson: any;
        try {
          parsedJson = JSON.parse(content);
        } catch (parseError) {
          setStatusMessage({
            type: 'error',
            message: `Invalid JSON format: ${parseError instanceof Error ? parseError.message : 'Unknown parsing error'}`
          });
          return;
        }

        // Validate that it's a valid prompt profile structure
        if (!parsedJson || typeof parsedJson !== 'object') {
          setStatusMessage({
            type: 'error',
            message: 'Invalid profile format: Expected a JSON object.'
          });
          return;
        }

        // Check for required fields
        const requiredFields = ['name', 'prompt_type', 'asset_type'];
        const missingFields = requiredFields.filter(field => !parsedJson[field]);
        
        if (missingFields.length > 0) {
          setStatusMessage({
            type: 'error',
            message: `Missing required fields: ${missingFields.join(', ')}. Please ensure the JSON contains a valid prompt profile.`
          });
          return;
        }

        // Validate field types
        if (typeof parsedJson.name !== 'string' || parsedJson.name.trim() === '') {
          setStatusMessage({
            type: 'error',
            message: 'Profile name must be a non-empty string.'
          });
          return;
        }

        if (typeof parsedJson.prompt_type !== 'string' || parsedJson.prompt_type.trim() === '') {
          setStatusMessage({
            type: 'error',
            message: 'Prompt type must be a non-empty string.'
          });
          return;
        }

        if (typeof parsedJson.asset_type !== 'string' || parsedJson.asset_type.trim() === '') {
          setStatusMessage({
            type: 'error',
            message: 'Asset type must be a non-empty string.'
          });
          return;
        }

        // Ensure version field is present (required for new profiles)
        if (!parsedJson.version) {
          parsedJson.version = "1.0.0";
        }

        // Format the JSON for better readability
        const formattedJson = JSON.stringify(parsedJson, null, 2);
        
        // Update the editor with the imported content
        setJsonValue(formattedJson);
        validateJson(formattedJson);
        
        // Clear any previous error messages and show success
        setTimeout(() => {
          setStatusMessage({
            type: 'success',
            message: `Profile imported successfully! Loaded: ${parsedJson.name || 'Unnamed Profile'}`
          });
        }, 100);

      } catch (error) {
        console.error('Error importing profile:', error);
        setStatusMessage({
          type: 'error',
          message: `Failed to import profile: ${error instanceof Error ? error.message : 'Unknown error occurred'}`
        });
      }
    };

    reader.onerror = () => {
      setStatusMessage({
        type: 'error',
        message: 'Failed to read the selected file. Please try again.'
      });
    };

    reader.readAsText(file);
  };

  return (
    <Container>
      <Header>
        <Title>JSON Prompt Profile Editor</Title>
        <ModeToggle>
          <span style={{ fontSize: '14px', color: '#6c757d' }}>Edit Mode:</span>
          <ToggleButton $active={true}>
            JSON Editor
          </ToggleButton>
        </ModeToggle>
      </Header>

      <Content>
        <TemplateContainer>
          <span style={{ fontSize: '14px', color: '#6c757d' }}>Template:</span>
          <TemplateSelector onChange={(e) => handleTemplateChange(e.target.value)}>
            <option value="">Select Template...</option>
            {TemplateService.getTemplateNames().map(key => (
              <option key={key} value={key}>{key}</option>
            ))}
          </TemplateSelector>
        </TemplateContainer>

        {statusMessage && (
          <StatusMessage $type={statusMessage.type}>
            {statusMessage.message}
          </StatusMessage>
        )}

        <JsonContainer>
          <JsonEditor
            value={jsonValue}
            onChange={handleJsonChange}
            placeholder="Enter prompt profile JSON..."
            label="Prompt Profile JSON"
            showToolbar={true}
            showStatusBar={true}
            validationSchema={PROMPT_PROFILE_VALIDATION_SCHEMA}
            templates={PROMPT_PROFILE_TEMPLATES}
            helpText="Edit the prompt profile configuration in JSON format. Use the toolbar for common actions."
          />
        </JsonContainer>

        <ActionsContainer>
          <ActionButtons>
            <label>
              <input
                type="file"
                accept=".json"
                onChange={handleImport}
                style={{ display: 'none' }}
              />
              <Button as="span">Import JSON</Button>
            </label>
            <Button onClick={handleExport} disabled={!jsonValue.trim()}>
              Export JSON
            </Button>
            <Button onClick={onCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button 
              $variant="primary" 
              onClick={handleSave} 
              disabled={!isValid || isSaving}
            >
              {isSaving ? 'Saving...' : `Save ${mode === 'create' ? 'Profile' : 'Changes'}`}
            </Button>
          </ActionButtons>
        </ActionsContainer>
      </Content>
    </Container>
  );
});

export default JsonPromptProfileEditor;
